import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Parse } from 'parse';
import { lifeTaggerApi } from '../api/lifetaggerApi';
import { lifeTaggerDesign } from '../design/tokens';
import { lifeTaggerTheme } from '../design/LifeTaggerTheme';
import {
  invitationDeviceState,
  invitationPresentation,
  mobileStoreUrl,
} from './invitationState';

const openApp = (token, path = '') => {
  const target = path
    ? `lifetagger://${path.replace(/^\//, '')}`
    : `lifetagger://invitation?token=${encodeURIComponent(token)}`;
  window.location.assign(target);
};

export default function InvitationLanding({ match }) {
  const token = match.params.token;
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [claiming, setClaiming] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    lifeTaggerApi
      .getInvitationPreview(token)
      .then((value) => active && setPreview(value))
      .catch(
        () =>
          active &&
          setPreview({ state: navigator.onLine ? 'invalid' : 'offline' })
      );
    return () => {
      active = false;
    };
  }, [token, attempt]);

  const signedIn = Boolean(Parse.User.current());
  const effectiveState =
    preview?.state === 'ready' &&
    invitationDeviceState(navigator.userAgent) === 'unsupported'
      ? 'unsupported'
      : preview?.state;
  const presentation = invitationPresentation(effectiveState);

  const continueToMoment = async () => {
    setError(null);
    if (effectiveState === 'offline') {
      setPreview(null);
      setAttempt((value) => value + 1);
      return;
    }
    if (!signedIn) {
      openApp(token);
      return;
    }
    setClaiming(true);
    try {
      const claimed = await lifeTaggerApi.claimInvitation(token);
      openApp(token, claimed.appPath);
    } catch (failure) {
      setError(
        failure?.message ||
          'Sign in with the verified phone number this invitation belongs to.'
      );
    } finally {
      setClaiming(false);
    }
  };

  if (!preview) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Checking invitation" />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={lifeTaggerTheme}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#1C1915',
          color: '#FFF9F0',
          display: 'flex',
          alignItems: 'stretch',
          backgroundImage:
            'radial-gradient(circle at 50% 22%, rgba(245,138,51,.22), transparent 33%), linear-gradient(180deg, #252018 0%, #1C1915 72%)',
        }}
      >
        <Container maxWidth="sm" sx={{ display: 'flex', py: 3 }}>
          <Stack sx={{ width: '100%', minHeight: 'calc(100vh - 48px)' }}>
            <Box
              component="header"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <Box
                component="img"
                src="/images/lifeTagger_logo.png"
                alt="LifeTagger"
                sx={{
                  height: 38,
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </Box>

            <Stack spacing={3} sx={{ my: 'auto', py: 8 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: '50%',
                  border: '1px solid rgba(245,138,51,.6)',
                  boxShadow: '0 0 70px rgba(245,138,51,.3)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#F58A33',
                  fontSize: 34,
                }}
              >
                ◌
              </Box>
              <Typography
                sx={{ color: '#F58A33', letterSpacing: '.16em', fontSize: 12 }}
              >
                {presentation.eyebrow}
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: lifeTaggerDesign.type.headline,
                  fontSize: { xs: 50, sm: 64 },
                  lineHeight: 0.98,
                  maxWidth: 520,
                }}
              >
                {presentation.title}
              </Typography>
              <Typography
                sx={{
                  color: '#D8D0C5',
                  fontSize: 18,
                  lineHeight: 1.65,
                  maxWidth: 500,
                }}
              >
                {presentation.body}
              </Typography>
              {error && (
                <Box
                  sx={{
                    color: '#FFD6CE',
                    borderLeft: '2px solid #E96842',
                    pl: 2,
                  }}
                >
                  {error}
                </Box>
              )}
              {presentation.action && (
                <Stack spacing={1.5} sx={{ pt: 2 }}>
                  <Button
                    size="large"
                    variant="contained"
                    disabled={claiming}
                    onClick={continueToMoment}
                    sx={{ borderRadius: 99, py: 1.6, fontWeight: 700 }}
                  >
                    {claiming ? 'Opening your Moment…' : presentation.action}
                  </Button>
                  {effectiveState === 'ready' && (
                    <Button
                      size="large"
                      href={mobileStoreUrl(navigator.userAgent)}
                      sx={{ color: '#FFF9F0', borderRadius: 99 }}
                    >
                      Get LifeTagger
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>

            <Typography
              sx={{ color: '#918B82', fontSize: 12, lineHeight: 1.5 }}
            >
              The invitation does not reveal its message, place, or timing in
              your browser. LifeTagger verifies that it belongs to you before
              opening it.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
